import axios from "axios";
import API_URL from "./Config";
import {
  clearAuthStorage,
  getAuthToken,
  hasActiveSession,
  isTokenExpired,
  redirectToLogin,
} from "./utils/authUtils";

const axiosInstance = axios.create({
  baseURL: API_URL,
});

let refreshPromise = null;

const refreshAccessToken = async () => {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const refreshToken =
      localStorage.getItem("refresh_token") ||
      sessionStorage.getItem("refresh_token");

    if (!refreshToken) {
      return null;
    }

    console.log("Access token expired, refreshing...");

    try {
      const response = await axios.post(
        `${API_URL}/api/refresh`,
        { refresh_token: refreshToken },
        {
          headers: {
            Authorization: `Bearer ${refreshToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      const newAccessToken = response?.data?.access_token;
      const newRefreshToken = response?.data?.refresh_token;

      if (!newAccessToken) {
        throw new Error("Refresh API did not return access_token");
      }

      localStorage.setItem("authKey", newAccessToken);
      sessionStorage.setItem("authKey", newAccessToken);

      if (newRefreshToken) {
        localStorage.setItem("refresh_token", newRefreshToken);
        sessionStorage.setItem("refresh_token", newRefreshToken);
      }

      axiosInstance.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`;
      console.log("Refresh success");
      return newAccessToken;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

axiosInstance.interceptors.request.use(
  async (config) => {
    const isRefreshCall = String(config.url || "").includes("/api/refresh");
    let token = getAuthToken();

    // Proactively renew an expired access token before firing the request,
    // as long as a valid refresh token is still available. This avoids the
    // extra 401 round-trip and covers the very first call after expiry.
    if (!isRefreshCall && token && isTokenExpired(token) && hasActiveSession()) {
      const refreshed = await refreshAccessToken();
      if (refreshed) token = refreshed;
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Keep multipart requests intact.
    if (config.data instanceof FormData) {
      delete config.headers["Content-Type"];
    }

    return config;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config;
    const status = error?.response?.status;

    if (!originalRequest || status !== 401) {
      return Promise.reject(error);
    }

    const isRefreshRequest = String(originalRequest?.url || "").includes("/api/refresh");
    if (isRefreshRequest) {
      console.log("Refresh failed, logging out");
      clearAuthStorage();
      redirectToLogin();
      return Promise.reject(error);
    }

    if (originalRequest._retry) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      const newAccessToken = await refreshAccessToken();
      if (!newAccessToken) {
        throw new Error("No access token received from refresh");
      }

      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
      console.log("Retrying original request");
      return axiosInstance(originalRequest);
    } catch (refreshError) {
      console.log("Refresh failed, logging out");
      clearAuthStorage();
      redirectToLogin();
      return Promise.reject(refreshError);
    }
  }
);

export default axiosInstance;
